import React, { useState } from 'react';
import { Filter, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { CommonFlowsPanel } from './CommonFlowsPanel';
import { AllFiltersPanel } from './AllFiltersPanel';
import { FilterCategory, PresetFilter, FilterRecord } from '@/types/filterBuilder';

interface PresetCategory {
  id: string;
  title: string;
  presets: PresetFilter[];
}

interface FilterBuilderProps {
  categories: FilterCategory[];
  presetCategories: PresetCategory[];
  entityName: string;
  onApplyFilters: (filters: FilterRecord) => void;
  activeFilters?: FilterRecord | null;
  onClearFilters?: () => void;
}

export function FilterBuilder({
  categories,
  presetCategories,
  entityName,
  onApplyFilters,
  activeFilters,
  onClearFilters,
}: FilterBuilderProps) {
  const [activeTab, setActiveTab] = useState<string>('common-flows');
  const [isOpen, setIsOpen] = useState(false);

  const activeFilterCount = activeFilters
    ? Object.keys(activeFilters).filter((key) => {
        const value = activeFilters[key];
        return value !== null && value !== undefined && value !== '';
      }).length
    : 0;

  const handleApplyFilters = (filters: FilterRecord) => {
    onApplyFilters(filters);
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    onClearFilters?.();
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="ml-1">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[860px] w-full max-h-[90dvh] md:max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-4 pt-5 pb-0 md:px-6 md:pt-6 flex-shrink-0">
          <DialogTitle>Filter {entityName}</DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-4 pt-3 md:px-6 md:pb-6 md:pt-4 flex-1 overflow-hidden flex flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-2 mb-4 flex-shrink-0">
              <TabsTrigger value="common-flows" className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Common Flows
              </TabsTrigger>
              <TabsTrigger value="all-filters" className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                All Filters
              </TabsTrigger>
            </TabsList>

            <TabsContent value="common-flows" className="mt-0 flex-1 overflow-hidden">
              <CommonFlowsPanel
                presetCategories={presetCategories}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
              />
            </TabsContent>

            <TabsContent value="all-filters" className="mt-0 flex-1 overflow-hidden">
              <AllFiltersPanel
                categories={categories}
                onApplyFilters={handleApplyFilters}
              />
            </TabsContent>
          </Tabs>
        </div>
        {/* Footer with Clear All */}
        {activeFilterCount > 0 && (
          <div className="px-4 md:px-6 py-3 border-t flex flex-wrap gap-2 justify-between items-center flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={handleClearFilters}>
              Clear All Filters
            </Button>
            <span className="text-sm text-muted-foreground">
              {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default FilterBuilder;
